import { IconSingleFamily, IconMultiFamily, IconCommercial, IconCondo, IconTownHome, IconOther } from '~/components/properties/Icons';
import { ManualJournalEntryStatus } from '~/swagger/accounting/Api';
import { LegalEntityType } from '~/swagger/companies/Api';
import { BoardRequestStatus, VoteChoice } from '~/swagger/hoa/Api';
import { ConnectedAccountStatus } from '~/swagger/pay/Api';
import {
  OfflineTransactionPaymentMethod,
  PayoutMethod,
  TransactionPaymentMethod,
  MaintenanceRequestStatus,
  TransactionStatus,
  CannotSendTextMessagesReason,
  TransactionFailure,
  LeaseTemplateType,
  FlyerTemplate,
  LeaseMoveOutRequestStatus,
  BankAccountConnectionStatus,
  ChargeFrequency,
  ConditionOperator,
  WorkOrderStatus,
  NoteType,
  SubsidizerType,
  IdentificationType,
  SubsidyType,
  PropertyType,
  LeaseChargeLedgerEntryStatus,
  LeaseChargeLedgerEntryType,
  ManagementFeeType,
  ExternalTransactionType,
  ChartOfAccountType,
  OwnerContributionRequestStatus,
  TransactionType,
  TransactionDirection,
  VendorBillStatus,
} from '~/swagger/portal/Api';
import { Dict } from '~/utils/dict';
import { SurveyStatus, VoteAudience, VoteQuestionType } from '~/utils/hoaSurvey';
import type { JSX, Component } from 'solid-js';
import type { ChartOfAccountType as ChartOfAccountTypeValue, PropertyType as PropertyTypeValue } from '~/swagger/portal/Api';
export const genderTypes = [
  { label: 'male', value: 'male' },
  { label: 'female', value: 'female' },
];

export const emptyPlaceholder = '--';

export const boardRequestStatusDict = new Dict<BoardRequestStatus, { color: string }>([
  { value: BoardRequestStatus.Draft, label: 'Draft', meta: { color: 'text-gray-500' } },
  { value: BoardRequestStatus.BoardVoting, label: 'Board voting', meta: { color: 'text-warning' } },
  { value: BoardRequestStatus.ReadyForPmReview, label: 'Ready for PM review', meta: { color: 'text-info' } },
  { value: BoardRequestStatus.Approved, label: 'Approved', meta: { color: 'text-success' } },
  { value: BoardRequestStatus.Denied, label: 'Denied', meta: { color: 'text-error' } },
  { value: BoardRequestStatus.Cancelled, label: 'Cancelled', meta: { color: 'text-gray-400' } },
]);

export const voteChoiceDict = new Dict<VoteChoice, { color: string }>([
  { value: VoteChoice.Yes, label: 'Yes', meta: { color: 'text-success' } },
  { value: VoteChoice.No, label: 'No', meta: { color: 'text-error' } },
]);

export const surveyStatusDict = new Dict<SurveyStatus, { color: string; background: string }>([
  { value: SurveyStatus.Draft, label: 'Draft', meta: { color: 'text-text-03', background: 'bg-grey' } },
  { value: SurveyStatus.Open, label: 'Open', meta: { color: 'text-success', background: 'bg-success/10' } },
  { value: SurveyStatus.Closed, label: 'Closed', meta: { color: 'text-text-02', background: 'bg-separator/20' } },
]);

export const voteAudienceDict = new Dict<VoteAudience>([
  { value: VoteAudience.BoardMembers, label: 'Board members' },
  { value: VoteAudience.CommunityMembers, label: 'Community members' },
]);

export const voteQuestionTypeDict = new Dict<VoteQuestionType>([
  { value: VoteQuestionType.YesNo, label: 'Yes / no' },
  { value: VoteQuestionType.MultipleChoice, label: 'Multiple choice' },
  { value: VoteQuestionType.FreeText, label: 'Free text' },
]);

export const priorityTypes = ['urgent', 'high', 'medium', 'low', 'none'].map((item) => ({ label: item, value: item }));

// TODO 后端API 会减少状态类型
export const maintenanceStatusTypes: Array<{
  label: string;
  value: MaintenanceRequestStatus;
}> = [
  {
    label: 'Pending',
    value: MaintenanceRequestStatus.Pending,
  },
  {
    label: 'In progress',
    value: MaintenanceRequestStatus.InProgress,
  },
  {
    label: 'Closed',
    value: MaintenanceRequestStatus.Closed,
  },
  {
    label: 'AI processing',
    value: MaintenanceRequestStatus.AiProcessing,
  },
  {
    label: 'Waiting for work order',
    value: MaintenanceRequestStatus.WaitingForWorkOrder,
  },
];

export const getMaintenanceStatusOptions = (status: MaintenanceRequestStatus) => {
  if (status !== MaintenanceRequestStatus.AiProcessing) {
    return maintenanceStatusTypes.filter((item) => item.value !== MaintenanceRequestStatus.AiProcessing);
  }
  return maintenanceStatusTypes;
};

export const permissionToEnterTypes = (t: (str: string) => string) => [
  {
    label: t('Yes'),
    value: true,
  },
  {
    label: t('No'),
    value: false,
  },
];

export enum currentUpdateType {
  work = 'work',
  rental = 'rental',
  document = 'document',
  rentalInfo = 'rentalInfo',
  receivePayment = 'receivePayment',
}

export enum rentalApplicationPaymentStatus {
  processing = 'processing',
  readyToConnect = 'transunion-connection',
  confirmIdentity = 'manual-verification',
  submitted = 'application-submitted',
  questionnaire = 'questionnaire',
}
export const workOrderStatusTypes: Array<{
  label: string;
  value: WorkOrderStatus;
}> = [
  {
    label: 'Pending',
    value: WorkOrderStatus.Pending,
  },
  {
    label: 'In progress',
    value: WorkOrderStatus.InProgress,
  },
  {
    label: 'Completed',
    value: WorkOrderStatus.Completed,
  },
  {
    label: 'Closed',
    value: WorkOrderStatus.Closed,
  },
];

export const transactionStatusDict = new Dict([
  { value: TransactionStatus.Pending, label: 'Pending', meta: { color: 'text-text-03', bgColor: 'bg-[#FFF4D7]' } },
  { value: TransactionStatus.Failed, label: 'Failed', meta: { color: 'text-danger', bgColor: 'bg-danger-light' } },
  { value: TransactionStatus.Success, label: 'Success', meta: { color: 'text-success', bgColor: 'bg-success' } },
  { value: TransactionStatus.Cancelled, label: 'Cancelled', meta: { color: 'text-warning', bgColor: 'bg-warning-light' } },
  { value: TransactionStatus.Returned, label: 'Returned', meta: { color: 'text-text-03', bgColor: 'bg-[#E2E9F2]' } },
  { value: TransactionStatus.Refunded, label: 'Refunded', meta: { color: 'text-text-03', bgColor: 'bg-[#E2E9F2]' } },
  { value: TransactionStatus.Disputing, label: 'Disputing', meta: { color: 'text-text-03', bgColor: 'bg-[#E2E9F2]' } },
  { value: TransactionStatus.Disputed, label: 'Disputed', meta: { color: 'text-text-03', bgColor: 'bg-[#E2E9F2]' } },
]);

export const ownerContributionRequestStatusDict = new Dict([
  { value: OwnerContributionRequestStatus.Requested, label: 'Requested', meta: { color: 'text-primary', bgColor: 'bg-primary/10' } },
  { value: OwnerContributionRequestStatus.Pending, label: 'Pending', meta: { color: 'text-text-03', bgColor: 'bg-[#FFF4D7]' } },
  { value: OwnerContributionRequestStatus.Completed, label: 'Completed', meta: { color: 'text-success', bgColor: 'bg-success' } },
  { value: OwnerContributionRequestStatus.Cancelled, label: 'Cancelled', meta: { color: 'text-warning', bgColor: 'bg-warning-light' } },
]);

export const offlineTransactionPaymentMethodsDict = new Dict([
  {
    label: 'Check',
    value: OfflineTransactionPaymentMethod.Check,
  },
  {
    label: "Cashier's check",
    value: OfflineTransactionPaymentMethod.CashiersCheck,
  },
  {
    label: 'Money order',
    value: OfflineTransactionPaymentMethod.MoneyOrder,
  },
  {
    label: 'Cash',
    value: OfflineTransactionPaymentMethod.Cash,
  },
  {
    label: 'Other',
    value: OfflineTransactionPaymentMethod.Other,
  },
]);

export const enumToOptions = (enumObj: Record<string, string>, renderLabel?: (value: any) => JSX.Element) => {
  return Object.entries(enumObj).map(([key, value]) => ({ label: renderLabel ? renderLabel(value) : key, value }));
};

export enum BackendEntitiesEnum {
  Portfolios = 'portfolios',
  Properties = 'properties',
  Units = 'units',
  Leases = 'leases',
  Tenants = 'tenants',
  Vendors = 'vendors',
  MaintenanceRequests = 'maintenance-requests',
  AssociationsViolations = 'associations/violations',
}

export const isDebug = () => import.meta.env.DEV || ['test', 'demo'].some((host) => location.hostname.includes(host));
export const isDemo = () => ['demo'].some((host) => location.hostname.includes(host));

export const PM_MENU_ACCESS_LABELS = {
  accounting: 'Accounting',
  ai: 'AI',
  associations: 'Associations',
  communications: 'Communications',
  home: 'Home',
  leasing: 'Leasing',
  listing: 'Listing',
  maintenance: 'Maintenance',
  onboarding: 'Onboarding',
  properties: 'Properties',
  reports: 'Reports',
  settings: 'Settings',
  users: 'Users',
} as const;

export type PMMenuAccessKey = keyof typeof PM_MENU_ACCESS_LABELS;

export const PM_MENU_ACCESS_KEYS = Object.keys(PM_MENU_ACCESS_LABELS) as PMMenuAccessKey[];

export const paymentTypeOptions = (t: (str: string) => string) => [
  { label: t('Manual'), value: PayoutMethod.Manual },
  { label: t('Print check'), value: PayoutMethod.PrintCheck },
  { label: t('ACH'), value: PayoutMethod.Ach },
];

export const transactionPaymentMethodsDict = new Dict([
  { label: 'ACH', value: TransactionPaymentMethod.Ach },
  { label: 'Credit', value: TransactionPaymentMethod.Credit },
  { label: 'Debit card', value: TransactionPaymentMethod.DebitCard },
  { label: 'Wallet', value: TransactionPaymentMethod.Wallet },
  { label: 'Credit card', value: TransactionPaymentMethod.CreditCard },
  { label: 'Check', value: TransactionPaymentMethod.Check },
  { label: "Cashier's check", value: TransactionPaymentMethod.CashiersCheck },
  { label: 'Money order', value: TransactionPaymentMethod.MoneyOrder },
  { label: 'Cash', value: TransactionPaymentMethod.Cash },
  { label: 'Other', value: TransactionPaymentMethod.Other },
]);

export const transactionTypeDict = new Dict<TransactionType, { ledgerType: LeaseChargeLedgerEntryType }>([
  { label: 'Payment', value: TransactionType.Payment, meta: { ledgerType: LeaseChargeLedgerEntryType.Payment } },
  { label: 'Return', value: TransactionType.Return, meta: { ledgerType: LeaseChargeLedgerEntryType.Return } },
  { label: 'Refund', value: TransactionType.Refund, meta: { ledgerType: LeaseChargeLedgerEntryType.Return } },
  { label: 'Dispute', value: TransactionType.Dispute, meta: { ledgerType: LeaseChargeLedgerEntryType.Payment } },
  { label: 'Credit', value: TransactionType.Credit, meta: { ledgerType: LeaseChargeLedgerEntryType.Credit } },
  { label: 'Transfer', value: TransactionType.Transfer, meta: { ledgerType: LeaseChargeLedgerEntryType.Transfer } },
]);

export const transactionDirectionDict = new Dict([
  { label: 'Incoming', value: TransactionDirection.In },
  { label: 'Outgoing', value: TransactionDirection.Out },
]);

export const externalTransactionTypeDict = new Dict([
  { label: 'Plaid', value: ExternalTransactionType.Plaid },
  { label: 'Stripe', value: ExternalTransactionType.Stripe },
  { label: 'Zelle', value: ExternalTransactionType.Zelle },
  { label: 'Offline', value: ExternalTransactionType.Offline },
]);

export const leaseTemplateTypeDict = new Dict([
  { value: LeaseTemplateType.Lease, label: 'Lease' },
  { value: LeaseTemplateType.LeaseRenewal, label: 'Lease renewal' },
  { value: LeaseTemplateType.LateRentNotice, label: 'Late rent notice' },
  { value: LeaseTemplateType.Eviction, label: 'Eviction' },
  { value: LeaseTemplateType.SignatureOnly, label: 'Signature only' },
  { value: LeaseTemplateType.Information, label: 'Information' },
]);

export const imageType = ['.jpg', '.jpeg', '.png', '.webp'];

export const themeColors = [
  {
    label: 'blue',
    value: '#2F49D0',
  },
  {
    label: 'green',
    value: '#06603A',
  },
  {
    label: 'orange',
    value: '#B64F16',
  },
  {
    label: 'purple',
    value: '#4D0061',
  },
  {
    label: 'black',
    value: '#2D2D2D',
  },
];

export const MIN_AMOUNT = 1;
export const MAX_INPUT_AMOUNT = 2147483647;
export const MAX_INPUT_AMOUNT_2 = 1000000000000;

export const NO_LIMIT = 'noLimit';

export const LAST_DELETED_LEASE_ID = 'lastDeletedLeaseId';

export const REPORT_LOAD_FAILED_MARK = 'reportLoadFailedMark';

export const NEW_RENTAL_APPLICATION_REVIEW_STEP = Infinity;

export enum OnboardingStatus {
  GET_VERIFIED_CHECKLIST = 'get_verified_checklist',
  IMPORTING = 'importing',
  PAID = 'paid',
  USER_HIDE_ONBOARDING = 'user_hide_onboarding',
}

export enum OnboardingV3Status {
  V3_ADD_PROPERTIES_AND_UNITS = 'v3_add_properties_and_units',
  V3_SETUP_LEASES = 'v3_setup_leases',
  V3_BUSINESS_BASIC_INFO = 'v3_business_basic_info',
  V3_PAYMENT_COLLECTION = 'v3_payment_collection',
  V3_TENANT_SCREENING = 'v3_tenant_screening',
  V3_INSURANCE = 'v3_insurance',
  V3_PHONE_LINE_SETUP = 'v3_phone_line_setup',
  V3_TEXT_MESSAGE_ALERT = 'v3_text_message_alert',
  V3_BUSINESS_PRINCIPLES = 'v3_business_principles',
  V3_SEND_INVITES = 'v3_send_invites',
}

export enum OnboardingChecklistModalType {
  PhoneNumber = 'phoneNumber',
  PaymentAccounts = 'paymentAccounts',
  BusinessInfo = 'businessInfo',
  BankAccounts = 'bankAccounts',
  AddBankAccounts = 'addBankAccounts',
  StripeConnected = 'stripeConnected',
  PlaidConnected = 'plaidConnected',
  ReceivePayment = 'receivePayment',
  WelcomeVideo = 'welcomeVideo',
}

export const CannotSendTextMessagesReasonMap: Record<CannotSendTextMessagesReason, string> = {
  [CannotSendTextMessagesReason.HasNoPhoneNumber]: 'Has no phone number',
  [CannotSendTextMessagesReason.TcrCampaignNotApproved]: 'TCR campaign not approved',
};
export const transactionFailureDict = new Dict([
  { label: 'Insufficient funds', value: TransactionFailure.NoSufficientFunds },
  { label: 'Unknown', value: TransactionFailure.Unknown },
  { label: 'Transfer limit reached', value: TransactionFailure.TransferLimitReached },
  { label: 'Card lost', value: TransactionFailure.CardLost },
  { label: 'Stolen card', value: TransactionFailure.StolenCard },
  { label: 'Expired card', value: TransactionFailure.ExpiredCard },
  { label: 'Company account setup error', value: TransactionFailure.CompanyAccountSetupError },
  { label: 'Payment processor declined', value: TransactionFailure.PaymentProcessorDeclined },
  { label: 'Payment account setup error', value: TransactionFailure.PaymentAccountSetupError },
]);

export const flyers = new Dict([
  {
    label: "We're moving to MagicDoor!",
    value: FlyerTemplate.QrCode,
  },
  {
    label: "Exciting news! We're using MagicDoor",
    value: FlyerTemplate.TenantNotice,
  },
  {
    label: 'Welcome to your new home!',
    value: FlyerTemplate.Welcome,
  },
]);

export const IS_USER_LOGIN_FROM_INTERNAL_KEY = 'isUserLoginFromInternal';

export const LeaseMoveOutRequestStatusDict = new Dict([
  { label: 'Pending', value: LeaseMoveOutRequestStatus.Requested, meta: { color: 'text-warning', bgColor: 'bg-[#FFEBD8]' } },
  { label: 'Approved', value: LeaseMoveOutRequestStatus.Approved, meta: { color: 'text-success', bgColor: 'bg-[#C8FFEE]' } },
  { label: 'Rejected', value: LeaseMoveOutRequestStatus.Rejected, meta: { color: 'text-warning', bgColor: 'bg-[#E2E9F2]' } },
  { label: 'Retracted', value: LeaseMoveOutRequestStatus.Retracted, meta: { color: 'text-text-03', bgColor: 'bg-[#FFF1F6]' } },
]);

export const bankAccountConnectionStatusDict = new Dict(
  [
    {
      label: 'Pending',
      value: BankAccountConnectionStatus.Pending,
      meta: {
        bgColor: 'bg-[#FFF4D7]',
        textColor: 'text-[#F45A29]',
        circleColor: 'bg-[#F45A29]',
      },
    },
    {
      label: 'Connected',
      value: BankAccountConnectionStatus.Approved,
      meta: {
        bgColor: 'bg-[#C8FFEE]',
        textColor: 'text-[#009D7C]',
        circleColor: 'bg-[#009D7C]',
      },
    },
    {
      label: 'Denied',
      value: BankAccountConnectionStatus.Denied,
      meta: {
        bgColor: 'bg-[#FFF1F6]',
        textColor: 'text-[#DD3668]',
        circleColor: 'bg-[#DD3668]',
      },
    },
    {
      label: 'Incomplete',
      value: BankAccountConnectionStatus.Incomplete,
      meta: {
        bgColor: 'bg-[#E2E9F2]',
        textColor: 'text-[#6C80AF]',
        circleColor: 'bg-[#6C80AF]',
      },
    },
  ],
  {
    defaultMeta: {
      bgColor: 'bg-[#E2E9F2]',
      textColor: 'text-[#6C80AF]',
      circleColor: 'bg-[#6C80AF]',
    },
    labelPlaceholder: 'Not connected',
  }
);

export const connectedAccountStatusDict = new Dict([
  {
    label: 'Requested',
    value: ConnectedAccountStatus.Requested,
    meta: {
      textColor: 'text-primary',
      bgColor: 'bg-primary/10',
      circleColor: 'bg-primary',
    },
  },
  {
    label: 'Pending',
    value: ConnectedAccountStatus.Pending,
    meta: {
      textColor: 'text-warning',
      bgColor: 'bg-warning/10',
      circleColor: 'bg-warning',
    },
  },
  {
    label: 'Under Review',
    value: ConnectedAccountStatus.UnderReview,
    meta: {
      textColor: 'text-warning',
      bgColor: 'bg-warning/10',
      circleColor: 'bg-warning',
    },
  },
  {
    label: 'Approved',
    value: ConnectedAccountStatus.Approved,
    meta: {
      textColor: 'text-success',
      bgColor: 'bg-success/10',
      circleColor: 'bg-success',
    },
  },
  {
    label: 'Activated',
    value: ConnectedAccountStatus.Activated,
    meta: {
      textColor: 'text-success',
      bgColor: 'bg-success/10',
      circleColor: 'bg-success',
    },
  },
  {
    label: 'Rejected',
    value: ConnectedAccountStatus.Rejected,
    meta: {
      textColor: 'text-danger',
      bgColor: 'bg-danger/10',
      circleColor: 'bg-danger',
    },
  },
  {
    label: 'Denied',
    value: ConnectedAccountStatus.Denied,
    meta: {
      textColor: 'text-danger',
      bgColor: 'bg-danger/10',
      circleColor: 'bg-danger',
    },
  },
  {
    label: 'Suspended',
    value: ConnectedAccountStatus.Suspended,
    meta: {
      textColor: 'text-danger',
      bgColor: 'bg-danger/10',
      circleColor: 'bg-danger',
    },
  },
  {
    label: 'Exception',
    value: ConnectedAccountStatus.Exception,
    meta: {
      textColor: 'text-danger',
      bgColor: 'bg-danger/10',
      circleColor: 'bg-danger',
    },
  },
]);

export const chargeFrequencyOptionsDict = new Dict([
  { label: '1 payment monthly', value: ChargeFrequency.Monthly },
  { label: '2 payments monthly', value: ChargeFrequency.Monthly2XSplit },
  { label: 'Weekly', value: ChargeFrequency.Weekly },
  { label: 'Daily', value: ChargeFrequency.Daily },
  { label: 'Yearly', value: ChargeFrequency.Yearly },
]);

export const conditionOperatorsDict = new Dict<string, string>([
  { label: '=', value: ConditionOperator.Equal },
  { label: '!=', value: ConditionOperator.NotEqual },
  { label: '>', value: ConditionOperator.GreaterThan },
  { label: '<', value: ConditionOperator.LessThan },
  { label: '>=', value: ConditionOperator.GreaterThanOrEqual },
  { label: '<=', value: ConditionOperator.LessThanOrEqual },
]);
export enum LoginStep {
  MagicLinkInputEmail = 'MagicLinkInputEmail',
  OtpInputPhone = 'OtpInputPhone',
  OtpInputVerifyCode = 'OtpInputVerifyCode',
  Password = 'Password',
}

export enum LoginType {
  MagicLink = 'MagicLink',
  Otp = 'Otp',
  Password = 'Password',
}

export const lateFeeFormulaVariablesDict = new Dict([
  { label: 'Due', value: 'due', meta: { description: 'The amount currently due' } },
  { label: 'Total', value: 'total', meta: { description: 'The total amount of the bill' } },
  { label: 'Late Bills', value: 'lateBills', meta: { description: 'The number of previous late bills' } },
  { label: 'Late Days', value: 'lateDays', meta: { description: 'The number of late days' } },
  { label: 'Grace Period', value: 'gracePeriod', meta: { description: 'The grace period of the bill' } },
  { label: 'Grace Balance', value: 'graceBalance', meta: { description: 'The grace balance of the bill' } },
]);

export const lateFeeFormulaTemplates = new Dict([
  {
    label: 'Fixed on nth day, fixed daily from n+1 on',
    value: 'if(lateDays < n, 0, if(lateDays = n, fixedAmount1, fixedAmount2))',
  },
  {
    label: 'Percentage on nth, fixed daily from n+1 on',
    value: 'if(lateDays < n, 0, if(lateDays = n, due * percentage, fixedAmount))',
  },
]);

export const legalEntityTypeOptionsDict = new Dict([
  { label: 'Sole Proprietorship', value: LegalEntityType.SoleProprietorship },
  { label: 'LLC', value: LegalEntityType.Llc },
  { label: 'Private Company', value: LegalEntityType.PrivateCompany },
  { label: 'Public Company Unlisted', value: LegalEntityType.PublicCompanyUnlisted },
  { label: 'Public Company Listed', value: LegalEntityType.PublicCompanyListed },
  { label: 'Charitable Organization Not For Profit', value: LegalEntityType.CharitableOrganizationNotForProfit },
  { label: 'Partnership', value: LegalEntityType.Partnership },
]);

export const chargeFrequencys = (t: (str: string) => string) => [
  { label: t('Yearly'), value: ChargeFrequency.Yearly },
  { label: t('Quarterly'), value: ChargeFrequency.Quarterly },
  { label: t('Monthly'), value: ChargeFrequency.Monthly },
  { label: t('Twice a month'), value: ChargeFrequency.Monthly2XSplit },
  { label: t('Weekly'), value: ChargeFrequency.Weekly },
  { label: t('Daily'), value: ChargeFrequency.Daily },
];

export enum tenantTabs {
  newApplicant = 'new-applicant',
  existingTenant = 'existing-tenant',
}

export const noteTypeDict = new Dict([
  { label: 'Note', value: NoteType.Note },
  { label: 'Delinquency', value: NoteType.Delinquency },
]);

export const AccountingBasis = {
  Cash: 'cash',
  Accrual: 'accrual',
} as const;

export const accountingBasisDict = (t: (str: string) => string) =>
  new Dict([
    { label: t('Cash'), value: 'cash' },
    { label: t('Accrual'), value: 'accrual' },
  ]);

export const subsidizerTypeDict = new Dict([
  { label: 'Government', value: SubsidizerType.Government },
  { label: 'Non-Profit', value: SubsidizerType.NonProfit },
  { label: 'Private', value: SubsidizerType.Private },
  { label: 'Other', value: SubsidizerType.Other },
]);

export const identificationTypeDict = new Dict([
  { label: 'SSN', value: IdentificationType.Ssn },
  { label: 'EIN', value: IdentificationType.Ein },
]);

export const subsidyTypeDict = new Dict([
  { label: 'Section 8', value: SubsidyType.Section8 },
  { label: 'VASH', value: SubsidyType.Vash },
  { label: 'EHV', value: SubsidyType.Ehv },
  { label: 'FUP', value: SubsidyType.Fup },
  { label: 'Project Based', value: SubsidyType.ProjectBased },
  { label: 'Other', value: SubsidyType.Other },
]);

export const subsidyFrequencyDict = new Dict([
  { label: 'Monthly', value: ChargeFrequency.Monthly },
  { label: 'Yearly', value: ChargeFrequency.Yearly },
  { label: 'Quarterly', value: ChargeFrequency.Quarterly },
]);

export const subsidyStatusMap = (t: (str: string) => string) => ({
  active: {
    label: t('Active'),
    color: 'bg-success/10 text-success',
  },
  inactive: {
    label: t('Inactive'),
    color: 'bg-text-03/10 text-text-03',
  },
});

export const vendorBillStatusDict = new Dict<VendorBillStatus>([
  { value: VendorBillStatus.Draft, label: 'Draft', meta: { color: 'bg-text-03/10 text-text-03' } },
  { value: VendorBillStatus.Pending, label: 'Pending', meta: { color: 'bg-warning/10 text-warning' } },
  { value: VendorBillStatus.Approved, label: 'Approved', meta: { color: 'bg-success/10 text-success' } },
  { value: VendorBillStatus.Rejected, label: 'Rejected', meta: { color: 'bg-danger/10 text-danger' } },
  { value: VendorBillStatus.Withdrawn, label: 'Withdrawn', meta: { color: 'bg-text-03/10 text-text-03' } },
]);

export const settlementStatusDict = new Dict<'settled' | 'unsettled', { color: string }>([
  { value: 'settled', label: 'Settled', meta: { color: 'text-success' } },
  { value: 'unsettled', label: 'Unsettled', meta: { color: 'text-warning' } },
]);

export const subsidizerTypeStatusMap = (t: (str: string) => string) => ({
  government: {
    label: t('Government'),
    color: 'bg-primary/10 text-primary',
  },
  nonProfit: {
    label: t('Non-Profit'),
    color: 'bg-success/10 text-success',
  },
  private: {
    label: t('Private'),
    color: 'bg-warning/10 text-warning',
  },
  other: {
    label: t('Other'),
    color: 'bg-text-03/10 text-text-03',
  },
});

export const subsidizerStatusFilterDict = new Dict([
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
]);

export enum ListingStatus {
  Unlisted = 'Unlisted',
  Listed = 'Listed',
}

export const listingStatusDict = new Dict([
  { label: 'Unlisted', value: ListingStatus.Unlisted },
  { label: 'Listed', value: ListingStatus.Listed },
]);

export const propertyTypesDict = new Dict<PropertyTypeValue, { icon: Component<{ class: string }> }>([
  { label: 'Single family', value: PropertyType.SingleFamily, meta: { icon: IconSingleFamily } },
  { label: 'Multi family', value: PropertyType.MultiFamily, meta: { icon: IconMultiFamily } },
  { label: 'Commercial', value: PropertyType.Commercial, meta: { icon: IconCommercial } },
  { label: 'Condo', value: PropertyType.Condo, meta: { icon: IconCondo } },
  { label: 'Townhome', value: PropertyType.TownHome, meta: { icon: IconTownHome } },
  { label: 'Others', value: PropertyType.Other, meta: { icon: IconOther } },
]);

export const leaseTransactionStatusDict = new Dict([
  { label: 'Future', value: LeaseChargeLedgerEntryStatus.Future },
  { label: 'Due', value: LeaseChargeLedgerEntryStatus.Due },
  { label: 'Past due', value: LeaseChargeLedgerEntryStatus.PastDue },
  { label: 'Paid', value: LeaseChargeLedgerEntryStatus.Paid },
  { label: 'Failed', value: LeaseChargeLedgerEntryStatus.Failed },
  { label: 'Returned', value: LeaseChargeLedgerEntryStatus.Returned },
  { label: 'Processing', value: LeaseChargeLedgerEntryStatus.Processing },
  { label: 'Settled', value: LeaseChargeLedgerEntryStatus.Settled },
]);

export const managementFeeTypeDict = new Dict([
  { label: 'Fixed', value: ManagementFeeType.Fixed },
  { label: 'Percentage', value: ManagementFeeType.Percentage },
]);

export enum SpecialEntityType {
  ManagementFee = 'ManagementFee',
  ServiceBill = 'ServiceBill',
}

export const specialEntityTypeDict = new Dict([
  { label: 'Management fee', value: SpecialEntityType.ManagementFee },
  { label: 'Service bill', value: SpecialEntityType.ServiceBill },
]);

export const chartOfAccountTypeDict = new Dict<ChartOfAccountTypeValue, { range: [number, number] }>([
  { label: 'Asset', value: ChartOfAccountType.Asset, meta: { range: [100000, 199999] } },
  { label: 'Liability', value: ChartOfAccountType.Liability, meta: { range: [200000, 299999] } },
  { label: 'Equity', value: ChartOfAccountType.Equity, meta: { range: [300000, 399999] } },
  { label: 'Revenue', value: ChartOfAccountType.Revenue, meta: { range: [400000, 499999] } },
  { label: 'Expense', value: ChartOfAccountType.Expense, meta: { range: [600000, 699999] } },
  { label: 'Other revenue', value: ChartOfAccountType.OtherRevenue, meta: { range: [700000, 799999] } },
  { label: 'Other expense', value: ChartOfAccountType.OtherExpense, meta: { range: [800000, 899999] } },
]);

export const checkStatusDict = new Dict<'true' | 'false', { color: string }>([
  { label: 'Valid', value: 'false', meta: { color: 'bg-success/10 text-success' } },
  { label: 'Voided', value: 'true', meta: { color: 'bg-danger/10 text-danger' } },
]);

export const manualJournalEntryStatusDict = new Dict([
  { label: 'Draft', value: ManualJournalEntryStatus.Draft, meta: { color: 'text-text-03', bgColor: 'bg-grey-light' } },
  {
    label: 'Pending approval',
    value: ManualJournalEntryStatus.PendingApproval,
    meta: { color: 'text-warning', bgColor: 'bg-warning-light' },
  },
  { label: 'Approved', value: ManualJournalEntryStatus.Approved, meta: { color: 'text-success', bgColor: 'bg-success-light' } },
  { label: 'Posted', value: ManualJournalEntryStatus.Posted, meta: { color: 'text-link', bgColor: 'bg-link-light' } },
  { label: 'Rejected', value: ManualJournalEntryStatus.Rejected, meta: { color: 'text-danger', bgColor: 'bg-danger-light' } },
]);
